import React, { useContext, Fragment, forwardRef } from 'react';
import classNames from 'classnames';
import {
  Link,
  PrimaryButton,
  DefaultButton,
} from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { theme } from '../../../styles';
import { I18n } from '../../../survey.types';
import { SurveyContextType } from '../../../survey-context';

function PrivacyStatement(props: { classes: any }): JSX.Element {
  const {
    classes,
  } = props;

  const i18n: I18n = useContext(I18nContext);

  return (
    <div className={classNames(classes.privacyStatement, 'survey-privacy-statement')}>
      <ReactTemplate
        model={{
          a: (text: string) => (
            <Link
              href="https://go.microsoft.com/fwlink/?LinkId=521839"
              className={classNames(classes.infoText, 'survey-info-text')}
              target="_blank"
              aria-label={i18n.getString(_TL_('Privacy statement (opens in a new tab)'))}
            >
              {`${text} `}
              <Glyphicon glyph="ba-OpenInNewWindow" />
            </Link>
          ),
        }}
        template={
          i18n.getString(_TL_('By pressing submit, your feedback will be used to improve Microsoft products and services. {{a}}Privacy statement{{/a}}'))
        }
      />
    </div>
  );
}

const EndingPageFooter = forwardRef((props: { onDone: any; classes: any }, ref: any): JSX.Element => {
  const {
    onDone,
    classes,
  } = props;

  const i18n: I18n = useContext(I18nContext);
  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    logActivity,
    getPageIndex,
  } = surveyContext;

  return (
    <div className={classNames(classes.buttons, 'buttons')}>
      <PrimaryButton
        className="done"
        text={i18n.getString(_TL_('Done'))}
        onClick={() => {
          onDone();
          logActivity(`page: ${getPageIndex()}, doneButton: done`);
        }}
        componentRef={ref}
      />
    </div>
  );
});

const QuestionPageFooter = forwardRef((props: {
  classes: any;
  isLastPage: boolean;
  onSubmit: any;
  onNext: any;
  onDismiss: any;
  isSubmitButtonDisabled: boolean;
}, ref: any): JSX.Element => {
  const {
    classes,
    isLastPage,
    onSubmit,
    onNext,
    onDismiss,
    isSubmitButtonDisabled,
  } = props;

  const i18n: I18n = useContext(I18nContext);
  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    logActivity,
    getPageIndex,
  } = surveyContext;

  return (
    <Fragment>
      {isLastPage ? <PrivacyStatement classes={classes} /> : <div className={classNames(classes.emptyBottomDiv)} />}
      <div className={classNames(classes.buttons, 'buttons')}>
        {isLastPage ?
          (<PrimaryButton
            className={classNames(classes.submitButton, 'submit')}
            text={i18n.getString(_TL_('Submit'))}
            onClick={() => { onSubmit(); logActivity(`page: ${getPageIndex()}, submitButton: submit`); }}
            disabled={isSubmitButtonDisabled}
            componentRef={ref}
          />) :
          (<PrimaryButton
            className={classNames(classes.nextButton, 'next')}
            text={i18n.getString(_TL_('Next'))}
            onClick={() => { onNext(); logActivity(`page: ${getPageIndex()}, nextButton: next`); }}
            disabled={isSubmitButtonDisabled}
            componentRef={ref}
          />)
        }
        <DefaultButton
          className="cancel"
          text={i18n.getString(_TL_('Cancel'))}
          onClick={() => { onDismiss('button'); logActivity(`page: ${getPageIndex()}, cancelButton: dismiss`); }}
        />
      </div>
    </Fragment>
  );
});

interface FooterProps {
  isLastPage: boolean;
  isEndingPage: boolean;
  onSubmit: any;
  onNext: any;
  onDone: any;
  onDismiss: any;
  isSubmitButtonDisabled: boolean;
}

export const Footer = forwardRef((props: FooterProps, ref): JSX.Element => {
  const {
    isEndingPage,
    isLastPage,
  } = props;

  const classes: any = useDefaultStyles(() => ({
    footer: {
      fontSize: 14,
      paddingTop: 12,
      color: theme.palette.black,
    },
    emptyBottomDiv: {
      marginBottom: 16,
    },
    privacyStatement: {
      marginBottom: 20,
      color: theme.palette.neutralSecondary,
      fontSize: '12px',
    },
    infoText: {
      fontWeight: 600,
      textDecoration: 'underline',
    },
    submitButton: {
      marginRight: 8,
    },
    nextButton: {
      marginRight: 8,
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      '& button': {
        width: '80px',
      },
    },
  }));

  return (
    <div className={classNames(classes.footer, 'survey-footer')}>
      {isEndingPage ?
        <EndingPageFooter {...props} classes={classes} ref={ref} /> :
        <QuestionPageFooter {...props} classes={classes} ref={ref} />}
    </div>
  );
});
